import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import getConfig from 'next/config'
import styled from '@emotion/styled'
import { Alert, Input, Button, space, Checkbox } from '@ticketswap/solar'
import { Formik } from 'formik'
import { string, object } from 'yup'
import { useMutation, gql } from '@apollo/client'
import { Mutation } from '@apollo/client/react/components'
import cookies from 'js-cookie'
import { AUTHENTICATION_COMPLETED } from '~/lib/tracking-events'
import { GENERATE_AUTH_TOKEN } from './generateAuthToken'
import { track, setTrackingIdentity } from '~/utils'
import { ViewerConsumer, useViewer } from '../ViewerContext'
import getViewer from '~/lib/get-viewer'

const InputWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: ${space[8]};
`

const StyledCheckbox = styled(Checkbox)`
  margin: ${space[16]} 0;
`

const StyledAlert = styled(Alert)`
  margin-bottom: ${space[8]};
`

const schema = object().shape({
  firstname: string().required(),
  lastname: string().required(),
})

const REGISTER_USER = gql`
  mutation registerUserUsingEmail($input: RegisterUserUsingEmailInput!) {
    registerUserUsingEmail(input: $input) {
      userId
      apiAccessToken {
        tokenString
        expiresAt
      }
      errors {
        message
        code
      }
    }
  }
`

const GIVE_CONSENT = gql`
  mutation giveConsent($input: GiveConsentInput!) {
    giveConsent(input: $input) {
      errors {
        message
        code
      }
    }
  }
`

const { CLIENT_ID, TOKEN_COOKIE_NAME } = getConfig().publicRuntimeConfig

const SignupEmail = ({ t, onSignupComplete }) => {
  const [error, setError] = useState(null)
  const [userLoading, setUserLoading] = useState(false)
  const [marketingConsentGiven, setMarketingConsentGiven] = useState(false)
  const firstNameInput = useRef()
  const { setViewer } = useViewer()
  const [giveConsentMutation, { client }] = useMutation(GIVE_CONSENT)

  useEffect(() => {
    firstNameInput.current.focus()
  }, [])

  const handleSubmit = async submitted => {
    setError(null)

    const {
      data: {
        authenticateClientForToken: { token },
      },
    } = await submitted.generate({
      variables: {
        input: {
          clientId: CLIENT_ID,
        },
      },
    })

    const {
      data: { registerUserUsingEmail },
    } = await submitted.register({
      variables: {
        input: {
          ...submitted.values,
          redirectTo: window.location.href,
          token,
        },
      },
    })

    if (registerUserUsingEmail.errors.length === 0) {
      setUserLoading(true)

      const {
        apiAccessToken: { tokenString },
      } = registerUserUsingEmail

      cookies.set(TOKEN_COOKIE_NAME, tokenString, {
        expires: 30,
        secure: true,
        sameSite: 'lax',
      })

      await client.resetStore()
      const user = await getViewer(client)
      setViewer(user)
      setTrackingIdentity(user)

      if (marketingConsentGiven) {
        await giveConsentMutation({
          variables: {
            input: {
              consentType: 'MARKETING',
              hasGivenConsent: true,
            },
          },
        })
      }

      track(AUTHENTICATION_COMPLETED, {
        method: 'email',
        isNew: true,
      })

      onSignupComplete()
    } else {
      setError(registerUserUsingEmail.errors[0].message)

      track(AUTHENTICATION_COMPLETED, {
        method: 'email',
        isNew: true,
        error: registerUserUsingEmail.errors[0].code,
      })
    }
  }

  return (
    <ViewerConsumer>
      {({ email }) => (
        <Mutation mutation={GENERATE_AUTH_TOKEN}>
          {(generate, { loading: generating }) => (
            <Mutation mutation={REGISTER_USER}>
              {(register, { loading }) => (
                <Formik
                  initialValues={{
                    email,
                    firstname: '',
                    lastname: '',
                  }}
                  validationSchema={schema}
                  onSubmit={values =>
                    handleSubmit({
                      values,
                      generate,
                      register,
                    })
                  }
                >
                  {props => {
                    const { values, handleSubmit, handleChange, isValid } = props

                    return (
                      <form onSubmit={handleSubmit}>
                        <InputWrapper>
                          <Input
                            label={t('auth.signupEmail.fields.firstname.label')}
                            type="text"
                            ref={firstNameInput}
                            id="firstname"
                            name="firstname"
                            value={values.firstname}
                            onChange={handleChange}
                          />

                          <Input
                            label={t('auth.signupEmail.fields.lastname.label')}
                            type="text"
                            id="lastname"
                            name="lastname"
                            value={values.lastname}
                            onChange={handleChange}
                          />
                        </InputWrapper>

                        <StyledCheckbox
                          id="marketingConsent"
                          name="marketingConsent"
                          label={t('auth.consent.label')}
                          onChange={event => setMarketingConsentGiven(event.target.checked)}
                        />
                        {error && <StyledAlert variant="error">{error}</StyledAlert>}
                        <Button
                          variant="success"
                          type="submit"
                          width="full"
                          loading={generating || loading || userLoading}
                          disabled={!isValid}
                        >
                          {t('auth.signupEmail.button')}
                        </Button>
                      </form>
                    )
                  }}
                </Formik>
              )}
            </Mutation>
          )}
        </Mutation>
      )}
    </ViewerConsumer>
  )
}

SignupEmail.propTypes = {
  t: PropTypes.func.isRequired,
  onSignupComplete: PropTypes.func.isRequired,
}

export default SignupEmail
